import { isElement, type TNode } from '@udecode/plate'
import type { SerializedNode } from '~publish/legacy/editor/BufferEditor/types'
import type { PlainText } from '~publish/legacy/editor/BufferEditor/types.plate'
import type { WithPosition } from '~publish/legacy/editor/helpers/WithPosition'
import type { AutocompleteData } from '../../autocomplete'
import type {
  AutocompleteInputElement,
  AutocompleteInputElementInterface,
} from '../../autocomplete/nodes'

export const LINKEDIN_MENTION_INPUT_ELEMENT_TYPE = 'linkedin-mention-input'
export const LINKEDIN_MENTION_INPUT_ELEMENT_TRIGGER = '@'
export const LINKEDIN_MENTION_INPUT_VALID_TEXT_REGEX = /([a-zA-Z0-9 ._-]+)/

export enum SupportedEntity {
  Company = 'company',
  School = 'school',
  Showcase = 'showcase',
}

/**
 * The detail properties of a linkedin annotation entity
 * after it's been parsed when loading the composer.
 * It is extended below to include the indices of the entity.
 */
export interface LinkedInAnnotationEntityDetails extends AutocompleteData {
  // for personal profiles it's a string, for organizations it's a number
  id: string | number
  entity: string
  vanityName: string
  localizedName: string
  link: string
}

export type LinkedInAnnotationEntity =
  WithPosition<LinkedInAnnotationEntityDetails>

export interface LinkedInAnnotationInputElement
  extends AutocompleteInputElement {
  type: typeof LINKEDIN_MENTION_INPUT_ELEMENT_TYPE
  trigger: typeof LINKEDIN_MENTION_INPUT_ELEMENT_TRIGGER
  children: PlainText[]
  valid: boolean
  autocompleted: boolean
  channelId?: string
  isPermissionError?: boolean
  data?: LinkedInAnnotationEntityDetails
}

export type LinkedInAnnotationInputElementInterface = Required<
  AutocompleteInputElementInterface<LinkedInAnnotationInputElement>
>

export const LinkedInAnnotationInputElement: LinkedInAnnotationInputElementInterface =
  {
    type: LINKEDIN_MENTION_INPUT_ELEMENT_TYPE,
    trigger: LINKEDIN_MENTION_INPUT_ELEMENT_TRIGGER,
    validTextRegex: LINKEDIN_MENTION_INPUT_VALID_TEXT_REGEX,
    validMentionRegex: RegExp(
      `(${LINKEDIN_MENTION_INPUT_ELEMENT_TRIGGER}|)${LINKEDIN_MENTION_INPUT_VALID_TEXT_REGEX.source}`,
    ),
    is(node: TNode): node is LinkedInAnnotationInputElement {
      return (
        isElement(node) && node.type === LinkedInAnnotationInputElement.type
      )
    },

    new: (text = '', data?: LinkedInAnnotationEntityDetails) => {
      return {
        type: LinkedInAnnotationInputElement.type,

        children: [
          {
            text: data
              ? data.localizedName
              : `${LinkedInAnnotationInputElement.trigger}${text}`,
          },
        ],

        // If data is provided, the mention is autocompleted and valid
        // This is because all LinkedIn mentions are invalid until autocompleted
        valid: !!data,
        autocompleted: !!data,
        trigger: LinkedInAnnotationInputElement.trigger,
        data,
      }
    },

    toEntity(
      node: LinkedInAnnotationInputElement,
    ): LinkedInAnnotationEntityDetails | undefined {
      return node.data
    },

    serialize(
      node: LinkedInAnnotationInputElement,
    ): SerializedNode<LinkedInAnnotationEntityDetails | undefined> {
      return {
        text: '',
        data: LinkedInAnnotationInputElement.toEntity(node),
      }
    },
  }
